import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { apiFetch } from "@/utils/api";
import Link from "next/link";
import { ChevronRight, ChevronLeft } from "lucide-react";

interface Store {
  _id?: string;
  name: string;
  icon?: string;
  description?: string;
  website?: string;
  categories?: string[];
  category?: string; // Legacy field
  isActive?: boolean;
  slug?: string;
  coupons?: number;
  offersCount?: number;
  order?: number;
  isFeatured: boolean;
}

import { getSafeImageUrl } from "@/utils/images";

const PopularStoresSection = () => {
  const [stores, setStores] = useState<Store[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [featuredIconError, setFeaturedIconError] = useState(false);
  const [iconErrorMap, setIconErrorMap] = useState<{ [key: string]: boolean }>(
    {}
  );

  const mobileStoresRef = useRef<HTMLDivElement>(null);
  const desktopStoresRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    apiFetch<unknown>("/stores/?isActive=true&isFeatured=true")
      .then((res) => {
        let arr: unknown = [];
        if (Array.isArray(res)) {
          arr = res;
        } else if (res && typeof res === "object") {
          const obj = res as Record<string, unknown>;
          if (Array.isArray(obj.stores)) arr = obj.stores;
          else if (Array.isArray(obj.data)) arr = obj.data;
        }
        const filteredStores = Array.isArray(arr) ? arr.filter(
          (store) => 
            process.env.NEXT_PUBLIC_APP_TITLE === "OfferCounty" ? 
            (
              (store.coupons && store.coupons > 0) ||
              (store.offersCount && store.offersCount > 0)
            ) && 
            (store.isFeatured) 
            : 
            (store.isFeatured)
        ) : [];

        setStores(filteredStores.sort((a, b) => b.order - a.order));
        setError(
          filteredStores.length === 0 ? "No stores found." : null
        );
      })
      .catch((err: unknown) => {
        const message =
          err && typeof err === "object" && "message" in err
            ? (err as { message?: string }).message
            : undefined;
        setError(message || "Failed to load stores");
        setStores([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Separate featured store and other stores
  const featuredStore = stores?.[0];
  const otherStores = stores?.slice(1) || [];

  const scrollStores = (direction: 'left' | 'right', ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      // Scroll half a container width
      const scrollAmount = ref.current.offsetWidth / 2; 
      const currentScroll = ref.current.scrollLeft;

      if (direction === 'left') {
        ref.current.scroll({
          left: currentScroll - scrollAmount,
          behavior: 'smooth'
        });
      } else {
        ref.current.scroll({
          left: currentScroll + scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <div className="lg:px-4">
      <div className="mb-4 flex items-center justify-between">
        <div className="inline-block">
          <h2 className="relative text-2xl text-gray-700">Popular Stores</h2>
          <hr className="h-1 border-0 bg-primary-500 mt-2 w-full sm:w-[50%] rounded-lg" />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              scrollStores('left', mobileStoresRef); 
              scrollStores('left', desktopStoresRef);
            }}
            className="p-1 bg-gray-100 rounded-full shadow-md text-gray-600 hover:bg-gray-200 transition-colors"
            aria-label="Scroll Offers Left"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              scrollStores('right', mobileStoresRef); 
              scrollStores('right', desktopStoresRef);
            }}
            className="p-1 bg-gray-100 rounded-full shadow-md text-gray-600 hover:bg-gray-200 transition-colors"
            aria-label="Scroll Offers Right"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {featuredStore && (
        <Link
          href={featuredStore.slug ? `/store/${featuredStore.slug}` : "#"}
          className="flex justify-between h-[80px] items-center px-4 lg:py-3 mb-4 border border-gray-300 rounded-lg bg-white sm:hidden"
          prefetch={false}
        >
          {/* 1. Left Section: Text in two rows */}
          <div className="flex flex-col justify-center font-bold leading-tight text-gray-500 text-xs">
            <span>
              Featured Store
            </span>
            <span>
              Of The Month
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <Image
              src={
                featuredIconError
                  ? "/assets/OfferCodeFlight.jpg"
                  : getSafeImageUrl(featuredStore.icon || undefined)
              }
              alt={featuredStore.name || "Featured Store"}
              width={80}
              height={30}
              className="object-contain"
              onError={() => setFeaturedIconError(true)}
            />
          </div>

          <div className="flex items-center">
            <ChevronRight className="h-5 w-5 text-primary-500" />
          </div>
        </Link>
      )}

      <div className="relative">
        {/* Mobile View Carousel */}
        <div className="sm:hidden mb-4 relative">
          <div
            ref={mobileStoresRef} 
            className="grid gap-2 overflow-x-auto scrollbar-hide scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
            style={{
              gridTemplateRows: 'repeat(3, 65px)',
              gridAutoColumns: 'minmax(31.333%, 1fr)',
              gridAutoFlow: 'column dense',
            }}
          >
            {loading && (
              <>
                {/* Show enough loading boxes for 3 rows * 3 visible cols + 3 extra */}
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="animate-pulse bg-gray-200 rounded-sm w-full h-full" aria-busy="true" />
                ))}
              </>
            )}

            {!loading && otherStores.length > 0 && (
              <>
                {otherStores.map((store, i) => (
                  <Link
                    key={store._id || i}
                    href={store.slug ? `/store/${store.slug}` : "#"}
                    className="w-full h-full rounded-sm border border-gray-300 bg-white p-2 flex items-center justify-center relative overflow-hidden"
                    prefetch={false}
                  >
                    <Image
                      src={
                        iconErrorMap[store._id || i]
                          ? "/assets/OfferCodeFlight.jpg"
                          : getSafeImageUrl(store.icon)
                      }
                      alt={store.name}
                      width={60}
                      height={32}
                      className="object-contain"
                      onError={() =>
                        setIconErrorMap((map) => ({ ...map, [store._id || i]: true }))
                      }
                    />
                  </Link>
                ))}
              </>
            )}
            {error && !loading && <div className="text-center text-red-500 py-8 col-span-full">{error}</div>}
          </div>
        </div>

        {/* Desktop View Carousel */}
        <div className="hidden sm:flex">
          <div className="grid grid-rows-3 gap-4 mr-4">
            {/* Featured Store Card (Desktop) */}
            <div className="row-span-3 w-60  bg-white rounded-md border border-gray-300 hover:shadow-lg shadow flex flex-col items-center justify-between px-2 py-4 relative">
              <div className="flex flex-col items-center justify-between flex-1 w-full">
                <span className="text-gray-600 text-base font-semibold pb-2 border-b border-gray-300">
                  Featured store of the month
                </span>
  
                {featuredStore && (
                  <Link
                    href={featuredStore.slug ? `/store/${featuredStore.slug}` : "#"}
                    className="group transition-all duration-300 transform  hover:scale-95"
                    prefetch={false}
                  >
                    <div className="bg-white rounded-lg shadow-sm h-16 flex justify-center items-center mb-2">
                      <Image
                        src={
                          featuredIconError
                            ? "/assets/OfferCodeFlight.jpg"
                            : getSafeImageUrl(featuredStore.icon || undefined)
                        }
                        alt={featuredStore.name || "Featured Store"}
                        width={80}
                        height={32}
                        className="object-contain"
                        onError={() => setFeaturedIconError(true)}
                      />
                    </div>

                    <div className="text-gray-700 text-center text-base font-semibold mb-1">
                      {featuredStore.name || "Store Name"}
                    </div>
                    {((featuredStore.categories && featuredStore.categories.length > 0) || featuredStore.category) && (
                      <div className="text-primary-400 text-center text-xs font-medium">
                        {featuredStore.categories && featuredStore.categories.length > 0 
                          ? featuredStore.categories.join(', ')
                          : featuredStore.category}
                      </div>
                    )}
                  </Link>
                )}
                {featuredStore && (
                  <div className="w-full px-4 ">
                    <div className="bg-primary-50 rounded-md py-1 px-2 text-xs text-primary-700 font-semibold text-center">
                      {featuredStore.coupons} Coupons | {Number(featuredStore.offersCount) - Number(featuredStore.coupons)}{" "}
                      Offers
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            ref={desktopStoresRef}
            className="overflow-x-auto scrollbar-hide scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent pb-2"
            style={{
              scrollBehavior: "smooth",
              maxWidth: "calc(100vw - 18rem)",
            }}
          >
            <div className="grid grid-flow-col auto-cols-max grid-rows-3 gap-2 snap-x snap-mandatory">
              {loading &&
                Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse bg-gray-200 rounded-sm h-[75px] w-48 flex-shrink-0"
                    aria-busy="true"
                  />
                ))}
              {error && !loading && (
                <div className="text-center text-red-500 py-8 col-span-full">{error}</div>
              )}
              {!loading && otherStores.length > 0 &&
                otherStores.map((store, i) => (
                  <Link
                    key={store._id || i}
                    href={store.slug ? `/store/${store.slug}` : "#"}
                    className="group w-48 h-[75px] rounded-sm border border-gray-300 bg-white p-2 hover:shadow-xl transition-all duration-300 transform hover:scale-95 focus:outline-none focus:ring-2 focus:ring-primary-400 snap-center relative overflow-hidden"
                    prefetch={false}
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      <Image
                        src={
                          iconErrorMap[store._id || i]
                            ? "/assets/OfferCodeFlight.jpg"
                            : getSafeImageUrl(store.icon)
                        }
                        alt={store.name}
                        width={80}
                        height={32}
                        className="object-contain"
                        onError={() =>
                          setIconErrorMap((map) => ({
                            ...map,
                            [store._id || i]: true,
                          }))
                        }
                      />
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 bg-opacity-0 group-hover:bg-opacity-70 transition-all duration-300 opacity-0 group-hover:opacity-100">
                        <span className="text-white text-base font-semibold mb-1">
                          {store.name}
                        </span>
                        {((store.categories && store.categories.length > 0) || store.category) && (
                          <span className="text-primary-200 text-xs font-medium">
                            {store.categories && store.categories.length > 0 ? (
                              <>
                                {store.categories.slice(0, 2).join(', ')}
                                {store.categories.length > 2 && (
                                  <> +{store.categories.length - 2} more</>
                                )}
                              </>
                            ) : (
                              store.category
                            )}
                          </span>
                        )}
                        {store && (
                          <div className="w-full px-4 mt-1 ">
                            <div className="text-primary-200 text-xs font-medium text-center">
                              {store?.coupons} Coupons | {Number(store?.offersCount) - Number(store?.coupons)}{" "}
                              Offers
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopularStoresSection;